<template>
  <div class="call-analysis-content">
    <div v-if="callData" class="conversation-badges">
      <span v-if="isVoicemail" class="badge badge-voicemail" title="Voicemail / Automated System">
        <Icon icon="mdi:phone-outline" width="14" height="14" />
        <span>Voicemail / Automated System</span>
      </span>
      <span
        v-else-if="callData.isLiveConversation"
        class="badge badge-live"
        title="Live Conversation"
      >
        <Icon icon="mdi:comment-outline" width="14" height="14" />
        <span>Live Conversation</span>
      </span>
      <span
        v-if="callData.isConnectedConversation && !isVoicemail"
        class="badge badge-connected"
        :title="callData.connectReason || 'Connected Conversation'"
        v-tooltip="callData.connectReason"
      >
        <Icon icon="mdi:check-circle-outline" width="14" height="14" />
        <span>Connected</span>
      </span>
    </div>

    <div v-if="isVoicemail" class="voicemail-message">
      <p>Voicemail detected (not analyzed).</p>
    </div>

    <div
      v-if="!isVoicemail && analysisStatus && analysisStatus !== 'completed'"
      class="status-message"
      :class="{ 'status-message--failed': analysisStatus === 'failed' }"
    >
      <Icon
        v-if="analysisStatus !== 'failed'"
        icon="mdi:loading"
        width="18"
        height="18"
        class="spinning-icon"
      />
      <span v-if="analysisStatus === 'pending'">Analysis pending...</span>
      <span v-else-if="analysisStatus === 'in_progress'">Analyzing call...</span>
      <span v-else-if="analysisStatus === 'failed'" class="error-text">
        <Icon icon="mdi:alert-outline" width="16" height="16" class="error-icon-inline" />
        Analysis failed
        <button type="button" class="btn-retry" @click="$emit('regenerate')">Retry</button>
      </span>
    </div>

    <div
      v-if="!isVoicemail && transcriptUrl && transcriptStatus === 'completed'"
      class="section section-transcript"
    >
      <div class="transcript-header">
        <h6 class="section-title">Transcript</h6>
        <div class="transcript-actions">
          <button
            v-if="!showTranscript"
            type="button"
            class="btn-transcript-action btn-transcript-small"
            @click="showTranscript = true"
          >
            <Icon icon="mdi:eye-outline" width="14" height="14" />
            <span>View</span>
          </button>
          <button
            v-else
            type="button"
            class="btn-transcript-action btn-transcript-action-secondary btn-transcript-small"
            @click="showTranscript = false"
          >
            <Icon icon="mdi:eye-off-outline" width="14" height="14" />
            <span>Hide</span>
          </button>
          <button
            type="button"
            class="btn-transcript-action btn-transcript-small"
            :disabled="isDownloadingTranscript"
            @click="handleTranscriptDownload"
          >
            <Icon
              v-if="!isDownloadingTranscript"
              icon="mdi:arrow-down-circle-outline"
              width="14"
              height="14"
            />
            <Icon v-else icon="mdi:loading" width="14" height="14" class="spinning-icon" />
            <span>{{ isDownloadingTranscript ? "Downloading..." : "Download" }}</span>
          </button>
        </div>
      </div>
      <div v-if="showTranscript" class="transcript-content">
        <TranscriptViewer :transcript-url="transcriptUrl" @refresh="$emit('refresh-transcript')" />
      </div>
    </div>

    <div
      v-if="!isVoicemail && topics && Array.isArray(topics) && topics.length > 0"
      class="section section-topics"
    >
      <h6 class="section-title">Topics</h6>
      <div class="topics-container">
        <span v-for="(topic, index) in topics" :key="index" class="topic-chip">
          {{ topic }}
        </span>
      </div>
    </div>

    <div v-if="!isVoicemail && summary" class="section">
      <h6 class="section-title">Call Notes</h6>
      <div class="formatted-summary" v-html="formattedSummaryHtml"></div>
    </div>

    <div
      v-if="!isVoicemail && actionItems && Array.isArray(actionItems) && actionItems.length > 0"
      class="section"
    >
      <h6 class="section-title">Action Items</h6>
      <ul class="action-items-list">
        <li v-for="(item, index) in actionItems" :key="index" class="action-item">
          {{ item }}
        </li>
      </ul>
    </div>

    <div v-if="!isVoicemail && sentiment" class="section sentiment-section">
      <h6 class="section-title">Sentiment Analysis</h6>
      <div class="sentiment-container">
        <div class="sentiment-column">
          <div class="sentiment-label-text">Customer Sentiment:</div>
          <div
            class="sentiment-badge"
            :class="{
              'sentiment-badge-positive': sentiment.customer === 'Positive',
              'sentiment-badge-neutral': sentiment.customer === 'Neutral',
              'sentiment-badge-negative': sentiment.customer === 'Negative',
            }"
          >
            {{ sentiment.customer || "N/A" }}
          </div>
        </div>
        <div class="sentiment-column">
          <div class="sentiment-label-text">Sales Rep Sentiment:</div>
          <div
            class="sentiment-badge"
            :class="{
              'sentiment-badge-positive': sentiment.salesRep === 'Positive',
              'sentiment-badge-neutral': sentiment.salesRep === 'Neutral',
              'sentiment-badge-negative': sentiment.salesRep === 'Negative',
            }"
          >
            {{ sentiment.salesRep || "N/A" }}
          </div>
        </div>
      </div>
    </div>

    <!-- Call Metadata -->
    <div v-if="!isVoicemail && callMetadata" class="section">
      <h6 class="section-title">Call Metadata</h6>
      <div class="metadata-container">
        <div class="metadata-badges">
          <span v-if="callMetadata.type" class="metadata-badge metadata-badge--type">
            {{ callMetadata.type }}
          </span>
          <span v-if="callMetadata.outcome" class="metadata-badge metadata-badge--outcome">
            {{ callMetadata.outcome }}
          </span>
        </div>
        <div v-if="callMetadata.sentiment" class="metadata-sentiment">
          {{ callMetadata.sentiment }}
        </div>
      </div>
    </div>

    <!-- Key Insights / Golden Nuggets -->
    <div
      v-if="!isVoicemail && executiveSummary && executiveSummary.goldenNuggets.length > 0"
      class="section"
    >
      <h6 class="section-title">Key Insights</h6>
      <ul class="golden-nuggets-list">
        <li
          v-for="(nugget, idx) in executiveSummary.goldenNuggets"
          :key="idx"
          class="golden-nugget-item"
        >
          {{ nugget }}
        </li>
      </ul>
    </div>

    <!-- Prospect Intel -->
    <div v-if="!isVoicemail && detectiveAnalysis" class="section">
      <h6 class="section-title">Prospect Intel</h6>
      <div
        v-if="detectiveAnalysis.prospectQuestions && detectiveAnalysis.prospectQuestions.length > 0"
        class="intel-subsection"
      >
        <div class="intel-subsection-title">Questions Asked</div>
        <div
          v-for="(q, idx) in detectiveAnalysis.prospectQuestions"
          :key="idx"
          class="prospect-question"
        >
          <div class="prospect-question__text">{{ q.questionAsked }}</div>
          <div v-if="q.impliedNeed" class="prospect-question__implied">
            Implied need: {{ q.impliedNeed }}
          </div>
        </div>
      </div>
      <div
        v-if="detectiveAnalysis.painPoints && detectiveAnalysis.painPoints.length > 0"
        class="intel-subsection"
      >
        <div class="intel-subsection-title">Pain Points</div>
        <ul class="action-items-list">
          <li v-for="(point, idx) in detectiveAnalysis.painPoints" :key="idx" class="action-item">
            {{ point }}
          </li>
        </ul>
      </div>
    </div>

    <!-- Follow-Up Strategy -->
    <div v-if="!isVoicemail && followUpStrategy" class="section">
      <h6 class="section-title">Follow-Up Strategy</h6>
      <div v-if="followUpStrategy.suggestedAngle" class="followup-row">
        <span class="followup-label">Angle:</span>
        <span class="followup-value">{{ followUpStrategy.suggestedAngle }}</span>
      </div>
      <div v-if="followUpStrategy.openingHook" class="followup-row">
        <span class="followup-label">Opening Hook:</span>
        <span class="followup-value">{{ followUpStrategy.openingHook }}</span>
      </div>
      <div
        v-if="followUpStrategy.actionItems && followUpStrategy.actionItems.length > 0"
        class="followup-actions"
      >
        <div class="followup-label">Action Items:</div>
        <ul class="action-items-list">
          <li v-for="(item, idx) in followUpStrategy.actionItems" :key="idx" class="action-item">
            {{ item }}
          </li>
        </ul>
      </div>
    </div>

    <!-- Send Follow-Up Email Button -->
    <div
      v-if="!hideFollowUpEmail && !isVoicemail && followUpEmail && analysisStatus === 'completed'"
      class="section followup-email-action"
    >
      <button type="button" class="btn-send-followup" @click="handleSendFollowUp">
        <Icon icon="mdi:email-send-outline" width="16" height="16" />
        <span>Send Follow-Up Email</span>
      </button>
    </div>

    <div
      v-if="!isLoading && !hasAnalysisData && analysisStatus === 'completed'"
      class="no-data-state"
    >
      <p>No analysis data available for this call.</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { CallLogResponse } from "@/dtos/CallLog.dto";
import { toastService } from "@/utils/toast-service";
import { downloadTranscript } from "@/utils/transcript";
import { Icon } from "@iconify/vue";
import { computed, ref } from "vue";
import TranscriptViewer from "./TranscriptViewer.vue";

const props = defineProps<{
  callData: CallLogResponse | null;
  isLoading: boolean;
  // For hosts with no email composer to hand the follow-up off to.
  hideFollowUpEmail?: boolean;
}>();

const emit = defineEmits<{
  regenerate: [];
  "send-follow-up-email": [payload: { subject: string; content: string }];
  "refresh-transcript": [];
}>();
const showTranscript = ref(false);
const isDownloadingTranscript = ref(false);

const summary = computed(() => props.callData?.analysis?.summary || "");
const actionItems = computed(() => props.callData?.analysis?.actionItems || null);
const topics = computed(() => props.callData?.analysis?.topics || null);
const sentiment = computed(() => props.callData?.analysis?.sentiment || null);
const transcriptUrl = computed(() => props.callData?.transcriptUrl || null);
const analysisStatus = computed(() => props.callData?.analysisStatus || null);
const transcriptStatus = computed(() => props.callData?.transcriptStatus || null);
const callMetadata = computed(() => props.callData?.analysis?.callMetadata || null);
const executiveSummary = computed(() => props.callData?.analysis?.executiveSummary || null);
const detectiveAnalysis = computed(() => props.callData?.analysis?.detectiveAnalysis || null);
const followUpStrategy = computed(() => props.callData?.analysis?.followUpStrategy || null);
const followUpEmail = computed(() => props.callData?.analysis?.followUpEmail || null);

const formattedSummaryHtml = computed(() => {
  if (!summary.value) return "";

  const lines = summary.value.split("\n");
  const result: string[] = [];
  let inList = false;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmed = line.trim();

    if (!trimmed) {
      if (inList) {
        result.push("</ul>");
        inList = false;
      }
      continue;
    }

    if (trimmed.startsWith("**") && trimmed.endsWith("**")) {
      if (inList) {
        result.push("</ul>");
        inList = false;
      }
      const headerText = trimmed.replace(/\*\*/g, "").trim();
      result.push(`<div class="summary-header">${headerText}</div>`);
    } else if (trimmed.startsWith("- ")) {
      if (!inList) {
        result.push('<ul class="summary-list">');
        inList = true;
      }
      const itemText = trimmed.substring(2).trim();
      result.push(`<li class="summary-list-item">${itemText}</li>`);
    } else {
      if (inList) {
        result.push("</ul>");
        inList = false;
      }

      const isLikelyTopicName =
        trimmed.length < 50 &&
        !trimmed.endsWith(".") &&
        !trimmed.endsWith("?") &&
        !trimmed.endsWith("!") &&
        !trimmed.includes(". ") &&
        i + 1 < lines.length &&
        lines[i + 1]?.trim().startsWith("-");

      if (isLikelyTopicName) {
        result.push(`<div class="summary-topic-name">${trimmed}</div>`);
      } else {
        result.push(`<p class="summary-paragraph">${trimmed}</p>`);
      }
    }
  }

  if (inList) {
    result.push("</ul>");
  }

  return result.join("\n");
});

const isVoicemail = computed(() => {
  if (!props.callData) return false;
  return (
    !props.callData.isLiveConversation && props.callData.connectReason?.startsWith("Twilio AMD:")
  );
});

const hasAnalysisData = computed(() => {
  return (
    !!summary.value ||
    (actionItems.value && Array.isArray(actionItems.value) && actionItems.value.length > 0) ||
    (topics.value && Array.isArray(topics.value) && topics.value.length > 0) ||
    !!sentiment.value ||
    !!callMetadata.value ||
    !!executiveSummary.value ||
    !!detectiveAnalysis.value ||
    !!followUpStrategy.value
  );
});

function handleSendFollowUp() {
  if (!followUpEmail.value) return;
  const { subjectLine, emailBody } = followUpEmail.value;
  const hasBrTags = /<br\s*\/?>/i.test(emailBody);
  let htmlBody: string;
  if (hasBrTags) {
    htmlBody = emailBody
      .split(/(?:<br\s*\/?>\s*){2,}/i)
      .map((p: string) => `<p>${p.trim()}</p>`)
      .join("<p><br></p>");
  } else {
    htmlBody = emailBody
      .split(/\n\n+/)
      .map((p: string) => `<p>${p.replace(/\n/g, "<br>")}</p>`)
      .join("");
  }
  emit("send-follow-up-email", { subject: subjectLine, content: htmlBody });
}

async function handleTranscriptDownload() {
  if (!transcriptUrl.value || isDownloadingTranscript.value) {
    return;
  }

  isDownloadingTranscript.value = true;
  try {
    await downloadTranscript(transcriptUrl.value, props.callData ?? undefined);
  } catch (error) {
    const message = error instanceof Error ? error.message : "Failed to download transcript";
    toastService.error(message);
  } finally {
    isDownloadingTranscript.value = false;
  }
}
</script>

<style scoped>
.call-analysis-content {
  width: 100%;
}

.conversation-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 16px;
  margin-bottom: 12px;
}

.badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
}

.badge :deep(svg) {
  flex-shrink: 0;
}

.badge-live {
  background-color: #dcfce7;
  color: #16a34a;
}

.badge-connected {
  background-color: #dbeafe;
  color: #2563eb;
}

.badge-voicemail {
  background-color: #fee2e2;
  color: #dc2626;
}

.status-message {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px;
  background: #fef3c7;
  border: 1px solid #fcd34d;
  border-radius: 8px;
  margin-bottom: 0;
  font-size: 0.85rem;
  color: #92400e;
}

.status-message--failed {
  background: #fefce8;
  border-color: #fde68a;
  color: #92400e;
}

.spinning-icon {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.error-text {
  color: #92400e;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.8rem;
}

.error-icon-inline {
  color: #d97706;
  flex-shrink: 0;
}

.btn-retry {
  padding: 2px 10px;
  background: transparent;
  border: 1px solid #d97706;
  border-radius: 5px;
  color: #d97706;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  margin-left: 4px;
}

.btn-retry:hover {
  background: #d97706;
  color: #ffffff;
}

.voicemail-message {
  padding: 12px;
  background: #fee2e2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  margin-bottom: 20px;
  font-size: 0.85rem;
  color: #991b1b;
  text-align: center;
}

.section {
  margin-bottom: 0;
  padding-bottom: 20px;
  padding-top: 20px;
  border-bottom: 1px solid #e8e8e8;
}

.section:first-child {
  padding-top: 0;
}

.section:last-child {
  margin-bottom: 0;
  padding-bottom: 0;
  border-bottom: none;
}

.section-topics {
  margin-top: -6px;
}

.section-title {
  font-weight: 700;
  font-size: 0.95rem;
  color: #2c2c2e;
  margin-bottom: 12px;
  margin-top: 0;
}

.topics-container {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.topic-chip {
  display: inline-block;
  padding: 6px 12px;
  background: #e0e7ff;
  color: #3730a3;
  border-radius: 16px;
  font-size: 0.8rem;
  font-weight: 500;
}

.summary-text {
  font-size: 0.85rem;
  color: #2c2c2e;
  line-height: 1.6;
  margin: 0;
  padding: 0;
}

.formatted-summary {
  font-size: 0.85rem;
  color: #2c2c2e;
  line-height: 1.8;
}

.formatted-summary :deep(.summary-header) {
  display: block;
  font-weight: 700;
  font-size: 0.9rem;
  color: #2c2c2e;
  margin-top: 16px;
  margin-bottom: 8px;
}

.formatted-summary :deep(.summary-header:first-child) {
  margin-top: 0;
}

.formatted-summary :deep(.summary-paragraph) {
  margin: 0 0 12px 0;
  line-height: 1.6;
}

.formatted-summary :deep(.summary-list) {
  list-style: none;
  padding: 0;
  margin: 6px 0 12px 0;
}

.formatted-summary :deep(.summary-list-item) {
  padding: 2px 0 2px 20px;
  position: relative;
  line-height: 1.5;
  margin-bottom: 0;
}

.formatted-summary :deep(.summary-list-item::before) {
  content: "•";
  position: absolute;
  left: 8px;
  color: #3e3efe;
  font-weight: bold;
  font-size: 1.1rem;
}

.formatted-summary :deep(.summary-topic-name) {
  font-weight: 600;
  font-size: 0.88rem;
  color: #3730a3;
  margin-top: 12px;
  margin-bottom: 6px;
}

.formatted-summary :deep(br) {
  line-height: 1.6;
}

.action-items-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.action-item {
  font-size: 0.85rem;
  color: #2c2c2e;
  line-height: 1.8;
  padding-left: 20px;
  position: relative;
}

.action-item::before {
  content: "•";
  position: absolute;
  left: 8px;
  color: #3e3efe;
  font-weight: bold;
}

.section-transcript {
  padding-top: 20px;
  margin-top: -16px;
}

.status-message + .section-transcript {
  padding-top: 20px;
}

.transcript-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0;
}

.transcript-actions {
  display: flex;
  gap: 6px;
  align-items: center;
}

.btn-transcript-action {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 5px 10px;
  background-color: #3e3efe;
  border: 1px solid #3e3efe;
  border-radius: 6px;
  color: #ffffff;
  font-size: 0.7rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  text-decoration: none;
  white-space: nowrap;
  min-width: fit-content;
}

.btn-transcript-action:hover {
  background-color: #3230d6;
  border-color: #3230d6;
  transform: translateY(-1px);
  box-shadow: 0 2px 6px rgba(62, 62, 254, 0.3);
}

.btn-transcript-action:disabled,
.btn-transcript-action:disabled:hover {
  opacity: 0.65;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.btn-transcript-small {
  padding: 5px 10px;
  font-size: 0.7rem;
  gap: 4px;
}

.btn-transcript-action-secondary {
  background-color: #ffffff;
  border: 1px solid #3e3efe;
  color: #3e3efe;
}

.btn-transcript-action-secondary:hover {
  background-color: #f0f0ff;
  border-color: #3e3efe;
  color: #3e3efe;
  transform: translateY(-1px);
  box-shadow: 0 2px 6px rgba(62, 62, 254, 0.2);
}

.btn-transcript-action :deep(svg) {
  flex-shrink: 0;
}

.transcript-content {
  margin-top: 12px;
  padding: 0;
}

.transcript-error {
  margin-top: 12px;
  padding: 12px;
  background: #fee2e2;
  border: 1px solid #fecaca;
  border-radius: 8px;
}

.transcript-error .error-text {
  color: #991b1b;
  font-size: 0.85rem;
  margin: 0;
}

.loading-state {
  text-align: center;
  padding: 20px;
}

.spinner {
  border: 3px solid #f3f3f3;
  border-top: 3px solid #3e3efe;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin: 0 auto 16px;
}

.no-data-state {
  text-align: center;
  padding: 40px 20px;
  color: #646464;
  font-size: 0.85rem;
}

.sentiment-section {
  border-top: none;
  border-bottom: none;
  padding-top: 20px;
  padding-bottom: 0;
}

.sentiment-container {
  display: flex;
  gap: 24px;
  align-items: flex-start;
}

.sentiment-column {
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
}

.sentiment-label-text {
  font-size: 0.85rem;
  color: #9ca3af;
  font-weight: 400;
}

.sentiment-badge {
  display: inline-block;
  padding: 4px 10px;
  background-color: #f3f4f6;
  color: #6b7280;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 500;
  width: fit-content;
}

.sentiment-badge-positive {
  background-color: #f3f4f6;
  color: #16a34a;
}

.sentiment-badge-neutral {
  background-color: #f3f4f6;
  color: #6b7280;
}

.sentiment-badge-negative {
  background-color: #f3f4f6;
  color: #dc2626;
}

/* Call Metadata */
.metadata-container {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.metadata-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.metadata-badge {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 500;
}

.metadata-badge--type {
  background-color: #e0e7ff;
  color: #3730a3;
}

.metadata-badge--outcome {
  background-color: #d1fae5;
  color: #065f46;
}

.metadata-sentiment {
  font-size: 0.85rem;
  color: #6b7280;
}

/* Golden Nuggets */
.golden-nuggets-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.golden-nugget-item {
  font-size: 0.85rem;
  color: #2c2c2e;
  line-height: 1.5;
  padding: 8px 12px;
  background: linear-gradient(135deg, #fef9c3 0%, #fefce8 100%);
  border-left: 3px solid #eab308;
  border-radius: 0 6px 6px 0;
}

/* Prospect Intel */
.intel-subsection {
  margin-bottom: 12px;
}

.intel-subsection:last-child {
  margin-bottom: 0;
}

.intel-subsection-title {
  font-size: 0.8rem;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 8px;
}

.prospect-question {
  padding: 8px 12px;
  background: #f9fafb;
  border-radius: 6px;
  margin-bottom: 6px;
}

.prospect-question:last-child {
  margin-bottom: 0;
}

.prospect-question__text {
  font-size: 0.85rem;
  color: #1f2937;
  font-weight: 500;
  line-height: 1.4;
}

.prospect-question__implied {
  font-size: 0.78rem;
  color: #6b7280;
  margin-top: 4px;
  font-style: italic;
}

/* Follow-Up Strategy */
.followup-row {
  display: flex;
  gap: 8px;
  margin-bottom: 8px;
  font-size: 0.85rem;
}

.followup-label {
  font-weight: 600;
  color: #374151;
  flex-shrink: 0;
}

.followup-value {
  color: #4b5563;
  line-height: 1.5;
}

.followup-actions {
  margin-top: 8px;
}

/* Send Follow-Up Email Button */
.followup-email-action {
  display: flex;
  justify-content: center;
}

.btn-send-followup {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 20px;
  background-color: #3e3efe;
  border: none;
  border-radius: 8px;
  color: #ffffff;
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
}

.btn-send-followup:hover {
  background-color: #3230d6;
}
</style>
